import { ChangeDetectorRef, Component, OnInit, inject } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgGlyph } from "@ng-icons/core";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { LocationSelectorComponent } from "../../../../Common/Components/LocationSelector/LocationSelectorComponent";
import { LocationAddress } from "../../../../Common/Components/LocationSelector/Type/LocationSelectorTypes";
import { QtyInputSelectZeroDirective } from "../../../../Common/Components/QtyInput/QtyInputSelectZeroDirective";
import { RichTextEditorComponent } from "../../../../Common/Components/RichTextEditor/RichTextEditorComponent";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";
import { PartnerPublicRequestItem } from "../../Dto/PartnerPublicRequestModel";
import { PartnerPublicRequestStep } from "../../Dto/PartnerPublicRequestStateModel";
import { PartnerPublicRequestEffects } from "../../State/PartnerPublicRequestEffects";
import { PartnerPublicRequestState } from "../../State/PartnerPublicRequestState";

@Component({
  selector: "app-public-partner-request",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AppButtonComponent,
    LocationSelectorComponent,
    NgGlyph,
    QtyInputSelectZeroDirective,
    RichTextEditorComponent,
  ],
  templateUrl: "./PartnerPublicRequestPage.html",
  styleUrl: "./PartnerPublicRequestPage.scss",
})
export class PartnerPublicRequestPage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly partnerPublicRequestEffects = inject(PartnerPublicRequestEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly partnerPublicRequestState = inject(PartnerPublicRequestState);

  token = "";
  step: PartnerPublicRequestStep = 1;
  materialQuantities: Record<string, number> = {};
  private readonly descriptionPreviewCache = new Map<string, string>();

  readonly teamLeaderForm = this.fb.group({
    name: ["", [Validators.required, Validators.minLength(2)]],
    email: [""],
    phone: [""],
    address: this.fb.group({
      stateProvinceRegion: ["", [Validators.required]],
      countyDistrict: ["", [Validators.required]],
      subdistrictMunicipality: ["", [Validators.required]],
      communeVillage: ["", [Validators.required]],
      line: [""],
      zip: [""],
      code: [""],
      latitude: [null as number | null],
      longitude: [null as number | null],
    }),
    note: [""],
  });

  get selectedItems(): PartnerPublicRequestItem[] {
    return Object.entries(this.materialQuantities)
      .map(([materialId, qty]) => ({ materialId, qty: Number(qty) }))
      .filter((item) => Number.isInteger(item.qty) && item.qty > 0);
  }

  get selectedMaterialCount(): number {
    return this.selectedItems.length;
  }

  get selectedTotalQty(): number {
    return this.selectedItems.reduce((total, item) => total + item.qty, 0);
  }

  async ngOnInit(): Promise<void> {
    this.partnerPublicRequestState.reset();
    this.token = this.route.snapshot.paramMap.get("token") ?? "";
    if (!this.token) {
      this.showUnavailableState();
      return;
    }

    await this.partnerPublicRequestEffects.loadContext(this.token);
    this.deferDetectChanges();
  }

  onAddressChange(value: LocationAddress): void {
    this.teamLeaderForm.patchValue({
      address: {
        stateProvinceRegion: value.stateProvinceRegion,
        countyDistrict: value.countyDistrict,
        subdistrictMunicipality: value.subdistrictMunicipality,
        communeVillage: value.communeVillage,
        line: value.line,
        zip: value.zip,
        code: value.code,
        latitude: value.latitude,
        longitude: value.longitude,
      },
    });
  }

  updateMaterialQty(materialId: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const qty = Math.floor(Number(input.value));
    this.materialQuantities = {
      ...this.materialQuantities,
      [materialId]: (() => {
        if (Number.isFinite(qty) && qty > 0) {
          return qty;
        }
        return 0;
      })(),
    };
  }

  goToStep(nextStep: PartnerPublicRequestStep): void {
    if (nextStep === 2 && this.teamLeaderForm.invalid) {
      this.teamLeaderForm.markAllAsTouched();
      this.toastEffects.show("Please fill in your name and address.", "error");
      return;
    }

    if (nextStep === 3 && this.selectedItems.length === 0) {
      this.toastEffects.show("Choose at least one material quantity.", "error");
      return;
    }

    this.step = nextStep;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async submit(): Promise<void> {
    if (this.teamLeaderForm.invalid) {
      this.goToStep(1);
      return;
    }

    if (this.selectedItems.length === 0) {
      this.goToStep(2);
      return;
    }

    const formValue = this.teamLeaderForm.value;
    try {
      await this.partnerPublicRequestEffects.submitRequest(this.token, {
        teamLeader: {
          name: (formValue.name ?? "").trim(),
          email: (formValue.email ?? "").trim() || null,
          phone: (formValue.phone ?? "").trim() || null,
          address: {
            stateProvinceRegion: formValue.address?.stateProvinceRegion ?? "",
            countyDistrict: formValue.address?.countyDistrict ?? "",
            subdistrictMunicipality: formValue.address?.subdistrictMunicipality ?? "",
            communeVillage: formValue.address?.communeVillage ?? "",
            line: formValue.address?.line ?? "",
            zip: formValue.address?.zip ?? "",
            code: formValue.address?.code ?? "",
            latitude: formValue.address?.latitude ?? null,
            longitude: formValue.address?.longitude ?? null,
          },
        },
        note: (formValue.note ?? "").trim() || null,
        items: this.selectedItems,
      });
      this.step = 4;
      this.toastEffects.show("Draft request submitted.", "success");
      this.deferDetectChanges();
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to submit request."), "error");
    }
  }

  getMaterialQty(materialId: string): number {
    return this.materialQuantities[materialId] ?? 0;
  }

  getMaterialName(materialId: string): string {
    return this.partnerPublicRequestState.context()?.materials.find((material) => material.id === materialId)?.name ?? materialId;
  }

  getMaterialDescriptionPreview(materialId: string, description: string | null): string {
    if (!description) {
      return "";
    }

    const cached = this.descriptionPreviewCache.get(materialId);
    if (cached !== undefined) {
      return cached;
    }

    const parser = new DOMParser();
    const document = parser.parseFromString(description, "text/html");
    const preview = (document.body.textContent ?? "").replace(/\s+/g, " ").trim();
    this.descriptionPreviewCache.set(materialId, preview);
    return preview;
  }

  private showUnavailableState(): void {
    this.partnerPublicRequestState.setError(
      "This request link has expired or is unavailable. Public links expire 12 hours after they are generated.",
    );
    this.partnerPublicRequestState.setLoading(false);
    this.deferDetectChanges();
  }

  private deferDetectChanges(): void {
    setTimeout(() => this.cdr.detectChanges(), 0);
  }
}
